import React, { useEffect, useMemo, useState } from 'react';
import {
  Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet,
} from '../kit';
import { AdminLink } from '../adminNav';
import { Period, PeriodPicker, periodText, usePeriodParams } from '../period';
import {
  EMPTY_TAG_FILTER, LIFECYCLE_LABEL, LifecyclePill, TagFilter, TagFilterValue, describeTagFilter,
  tagFilterActive, tagFilterParams, toLifecycle, useTagOptions,
} from '../tags/shared';

/**
 * Members — who searches, ranked (owner's sheet, "Search Intel Tab": a
 * member-level view with name, lifecycle status, lifetime searches, searches
 * in the last X days and profile completion; "ranking of top 30 members by
 * lifetime search count").
 *
 * Backed by search/members (Backend/admin_search_members.py). Counts are
 * user_direct_search_history rows — the "Searches" User 360 shows — counted
 * in SQL; search_query_logs attempts (incl. blocked Explorer attempts) are an
 * optional column, never added in. The server ranks by the picked sort and
 * returns the top N; the KPIs cover every member that matches the filters,
 * not just the rows shown.
 */

const WINDOWS = ['7d', '30d', '90d', 'all', 'custom'];
const WINDOW_LABELS: Record<string, string> = {
  '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', all: 'Lifetime', custom: 'Custom range…',
};
const WINDOW_DAYS: Record<string, number | null> = { '7d': 7, '30d': 30, '90d': 90, all: null };

const RECENT = ['7', '30', '60', '90', '180', '365'];
const RECENT_LABELS: Record<string, string> = Object.fromEntries(RECENT.map(d => [d, `Searches in last ${d} days`]));

const TOP = ['10', '20', '30', '50', '100', '1000'];
const TOP_LABELS: Record<string, string> = Object.fromEntries(TOP.map(n => [n, n === '1000' ? 'All (up to 1,000)' : `Top ${n}`]));

const SORTS = ['lifetime_searches', 'recent_searches', 'period_searches', 'profile_pct', 'last_search_at',
  'search_days', 'attempts'];

const COLUMNS = ['rank', 'member', 'lifecycle', 'lifetime_searches', 'recent_searches', 'period_searches',
  'profile_pct', 'last_search_at'];
const OPTIONAL = ['search_days', 'attempts', 'logins', 'last_login_at', 'chats', 'saved_n', 'organisation',
  'plan_code', 'created_at', 'first_search_at', 'email'];

/** Keeps the name/e-mail search off every keystroke. */
function useDebounced(value: string, msDelay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), msDelay);
    return () => clearTimeout(t);
  }, [value, msDelay]);
  return v;
}

const n = (v: any) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

const Members: React.FC = () => {
  const [period, setPeriod] = useState<Period>({ preset: '90d', from: '', to: '' });
  const { params: pp } = usePeriodParams(period);
  const [recent, setRecent] = useState('90');
  const [top, setTop] = useState('30');
  const [sort, setSort] = useState('lifetime_searches');
  const [team, setTeam] = useState(false);
  const [everyone, setEveryone] = useState(false);
  const [qIn, setQIn] = useState('');
  const q = useDebounced(qIn);
  const [tagF, setTagF] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const tagOpts = useTagOptions();
  const tagActive = tagFilterActive(tagF);

  const params = useMemo(() => ({
    ...pp, recent_days: recent, limit: top, sort, q,
    exclude_team: team ? '0' : '1', min_searches: everyone ? '0' : '1',
    ...tagFilterParams(tagF),
  }), [pp, recent, top, sort, q, team, everyone, tagF]);
  const key = JSON.stringify(params);
  const res = useAdminGet('search', 'members', params, [key]);
  const d: any = res.data || {};
  const totals = d.totals || {};
  const periodLabel = periodText({ preset: pp.window || '90d', from: pp.from || '', to: pp.to || '' }, WINDOW_LABELS);
  const recentN = d.recent_days ?? Number(recent);

  const rows = useMemo(() => (d.rows || []).map((r: any) => ({
    ...r,
    member: r.name || r.email,
    lifecycle: r.lifecycle_label || LIFECYCLE_LABEL[toLifecycle(r.lifecycle)] || r.lifecycle || '',
    _lifecycle: r.lifecycle,
  })), [d.rows]);

  const labels: Record<string, string> = {
    rank: '#', member: 'Member', lifecycle: 'Status', lifetime_searches: 'Lifetime searches',
    recent_searches: `Last ${recentN} days`, period_searches: `In period (${periodLabel})`,
    profile_pct: 'Profile %', last_search_at: 'Last search', search_days: 'Search days',
    attempts: 'Attempts', logins: 'Logins', last_login_at: 'Last login', chats: 'Chats', saved_n: 'Saved',
    organisation: 'Organisation', plan_code: 'Plan', created_at: 'Joined', first_search_at: 'First search',
    email: 'E-mail',
  };
  const sortLabels: Record<string, string> = {
    lifetime_searches: 'Rank by lifetime searches', recent_searches: `Rank by last ${recentN} days`,
    period_searches: 'Rank by searches in period', profile_pct: 'Rank by profile %',
    last_search_at: 'Rank by most recent search', search_days: 'Rank by search days', attempts: 'Rank by attempts',
  };
  const changed = !!(qIn || team || everyone || tagActive || sort !== 'lifetime_searches' || top !== '30'
    || recent !== '90' || period.preset !== '90d');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="text-sm text-grayscale-1200 max-w-2xl">
          Members ranked by their searches (the ones User 360 counts). Dates are UTC.
          {!team && ' Team accounts on the analytics exclusion list are left out.'}
        </div>
        <PeriodPicker value={period} onChange={setPeriod} options={WINDOWS} labels={WINDOW_LABELS}
          presetDays={WINDOW_DAYS} hint="The period the “In period” column counts. Both days included (UTC)." />
      </div>

      {res.data && (
        <KPIRow>
          <KPI label="Members who searched" value={n(totals.members_with_search)}
            hint={d.total_matching !== undefined && d.total_matching !== totals.members_with_search
              ? `of ${n(d.total_matching)} matching` : 'matching the filters'} />
          <KPI label="Lifetime searches" value={n(totals.searches_lifetime)} />
          <KPI label={`Last ${recentN} days`} value={n(totals.searches_recent)} hint="searches" />
          <KPI label="In period" value={n(totals.searches_period)} hint={periodLabel} />
        </KPIRow>
      )}

      <Section title={`Members (${TOP_LABELS[top] || `Top ${top}`})`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {res.data && (
              <span className="text-xs text-grayscale-1100">
                {rows.length.toLocaleString()} shown of {n(d.total_matching)}
              </span>
            )}
            {changed && (
              <Btn small onClick={() => {
                setQIn(''); setTeam(false); setEveryone(false); setTagF(EMPTY_TAG_FILTER); setSort('lifetime_searches');
                setTop('30'); setRecent('90'); setPeriod({ preset: '90d', from: '', to: '' });
              }}>Reset</Btn>
            )}
            <Btn small onClick={res.reload}>Refresh</Btn>
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={qIn} onChange={setQIn} placeholder="Name, e-mail or organisation" />
          <Select value={top} onChange={setTop} options={TOP} labels={TOP_LABELS} />
          <Select value={sort} onChange={setSort} options={SORTS} labels={sortLabels} />
          <Select value={recent} onChange={setRecent} options={RECENT} labels={RECENT_LABELS} />
          <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 h-9"
            title="Include the accounts on the analytics exclusion list (our own)">
            <input type="checkbox" checked={team} onChange={e => setTeam(e.target.checked)} />
            Count the team
          </label>
          <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 h-9"
            title="Also list members who have never searched (0 lifetime searches)">
            <input type="checkbox" checked={everyone} onChange={e => setEveryone(e.target.checked)} />
            Include members with no searches
          </label>
        </div>
        <TagFilter value={tagF} onChange={v => setTagF(v)} />
        {tagActive && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Chip label="lifecycle / tags"
              value={describeTagFilter(tagF, k => tagOpts.tagByKey[k]?.label || k).join(' · ')}
              onClear={() => setTagF(EMPTY_TAG_FILTER)} />
          </div>
        )}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <DataTable key={sort} rows={rows} columns={COLUMNS} optionalColumns={OPTIONAL} labels={labels}
            defaultSort={sort === 'last_search_at' ? '-last_search_at' : `-${sort}`}
            csvName="search-members" searchable maxHeight="40rem"
            empty={tagActive || q ? 'No member matches these filters.' : 'No member has searched yet.'}
            render={{
              member: r => (
                <div className="flex flex-col">
                  <AdminLink to={{ group: 'user360', email: r.email }} title="Open in User 360">
                    {r.name || r.email}
                  </AdminLink>
                  {r.name && <span className="text-[11px] text-grayscale-1100">{r.email}</span>}
                </div>
              ),
              lifecycle: r => <LifecyclePill value={r._lifecycle} subtag={r.subtag} label={r.lifecycle_label} />,
              lifetime_searches: r => <span className="font-medium">{n(r.lifetime_searches)}</span>,
              recent_searches: r => n(r.recent_searches),
              period_searches: r => n(r.period_searches),
              profile_pct: r => (r.profile_pct === null || r.profile_pct === undefined ? '—' : `${r.profile_pct}%`),
              last_search_at: r => fmtDate(r.last_search_at),
              first_search_at: r => fmtDate(r.first_search_at),
              last_login_at: r => fmtDate(r.last_login_at),
              created_at: r => fmtDate(r.created_at),
              plan_code: r => r.plan_code || '—',
              organisation: r => r.organisation || '—',
            }} />
        )}
        <p className="text-xs text-grayscale-1100">
          Lifetime and last-{recentN}-days counts end now; “In period” follows the period picker. Attempts
          (optional column) are search_query_logs calls, including searches blocked for Explorers. Profile % is
          the completion score the member sees.
        </p>
      </Section>
    </div>
  );
};

export default Members;
